import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { type AutocompleteProps, Grid, ToggleButton, Tooltip } from '@mui/material';
import { Stack } from '@mui/system';
import type { ProjectListEntry } from '@poweralpha/common';
import { Activity, useCallback, useEffect, useMemo, useState } from 'react';
import { useProjectsListQuery } from '../../project/api';
import { useDebounce } from '../useDebounce';
import FormAutocomplete, { type CustomAutocompleteProps } from './FormAutocomplete';
import { useFieldContext } from './formContext';

function isOptionEqual(option: ProjectListEntry, value: ProjectListEntry) {
  return option.id === value.id;
}

interface Props<Multiple extends boolean | undefined, DisableClearable extends boolean | undefined>
  extends Omit<
      AutocompleteProps<ProjectListEntry, Multiple, DisableClearable, false>,
      'renderInput' | 'freeSolo' | 'options' | 'size'
    >,
    Pick<
      CustomAutocompleteProps<ProjectListEntry, Multiple, DisableClearable, false>,
      'size' | 'variant' | 'hideErrorText' | 'displayOnly'
    > {}

function FormProjectAutocomplete<
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
>(props: Props<Multiple, DisableClearable>) {
  const field = useFieldContext<ProjectListEntry | null>();
  const [filterOn, setFilterOn] = useState(true);
  const [inputValue, setInputValue] = useState('');
  const debouncedInputValue = useDebounce(inputValue, 300);

  const { data: projectList, isLoading } = useProjectsListQuery({
    apiOptions: {
      showArchived: !filterOn,
      name: debouncedInputValue.trim() || undefined,
    },
  });

  const handleFilterClick = useCallback(() => setFilterOn((prev) => !prev), []);
  const handleInputChange = useCallback((value: string) => setInputValue(value), []);

  useEffect(() => {
    setInputValue(field.state.value?.name ?? '');
  }, [field.state.value?.name]);

  const toggleButtonSize = useMemo(() => {
    switch (props.size) {
      case 'tiny':
        return { height: 25, size: undefined };
      case 'xs':
        return { height: 30, size: undefined };
      default:
        return { height: undefined, size: props.size ?? 'small' };
    }
  }, [props.size]);

  return (
    <Stack direction="row" spacing={1}>
      <Grid size="grow">
        <FormAutocomplete
          {...props}
          options={projectList?.rows ?? []}
          freeSolo={false}
          getOptionLabel={(option) => option?.name ?? ''}
          isOptionEqualToValue={isOptionEqual}
          loading={isLoading}
          inputValue={inputValue}
          onInputChange={(_, value) => handleInputChange(value)}
        />
      </Grid>
      <Activity mode={props.displayOnly || props.disabled ? 'hidden' : 'visible'}>
        <Tooltip title={filterOn ? 'Show all' : 'Show Archived'}>
          <ToggleButton
            value="filter"
            size={toggleButtonSize.size}
            selected={filterOn}
            onChange={handleFilterClick}
            sx={{ height: toggleButtonSize.height, width: toggleButtonSize.height }}
          >
            <FilterAltIcon />
          </ToggleButton>
        </Tooltip>
      </Activity>
    </Stack>
  );
}

export default FormProjectAutocomplete;
